import type SubjectAddress from '@Domain/Subjects/SubjectAddress';
import MarkWorkflowRunHandled from '@Application/WorkflowRuns/Handling/Commands/MarkWorkflowRunHandled';
import markWorkflowRunHandled from '@Application/WorkflowRuns/Handling/markWorkflowRunHandled';
import FetchWorkflowRun from '@Application/WorkflowRuns/WorkflowRun/Queries/FetchWorkflowRun';
import fetchWorkflowRun from '@Application/WorkflowRuns/WorkflowRun/fetchWorkflowRun';
import { theme } from '@Guidelines/Theme/theme';
import ignoreHandledError from '@UI/Helpers/ignoreHandledError';
import useStrictCurrentFacility from '@UI/Providers/CurrentFacilityProvider/useStrictCurrentFacility';
import { css } from '@emotion/react';
import { Check } from 'lucide-react';
import * as React from 'react';
import { useDispatch } from '@verclam/synapse';
import { Button } from '@verclam/ui-kit';

const containerStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.625rem;
	align-items: flex-start;
`;

const noteStyle = css`
	margin: 0;
	font-family: ${theme.fonts.body};
	font-size: 0.8125rem;
	line-height: 1.55;
	color: ${theme.colors.inkSecondary};
`;

type Props = {
	readonly subject: SubjectAddress;
	readonly preparationId: number;
};

/** Marks a preparation with a failed document as handled so it stops blocking a new one; reversible, hence no confirmation. */
const MarkHandledAction: React.FC<Props> = ({ subject, preparationId }) => {
	const dispatch = useDispatch();
	const { currentFacility } = useStrictCurrentFacility();
	const facilityId = currentFacility.id;
	const [isHandling, setIsHandling] = React.useState(false);

	const handleClick = React.useCallback(() => {
		setIsHandling(true);
		dispatch(markWorkflowRunHandled(new MarkWorkflowRunHandled(facilityId, subject, preparationId)))
			.then(() => dispatch(fetchWorkflowRun(new FetchWorkflowRun(facilityId, subject, preparationId))))
			.catch(ignoreHandledError)
			.finally(() => {
				setIsHandling(false);
			});
	}, [dispatch, facilityId, subject, preparationId]);

	return (
		<div css={containerStyle}>
			<p css={noteStyle} role="note">
				Un document n'a pas pu être généré. Si vous ne comptez pas le relancer, marquez la préparation
				comme traitée : elle ne bloquera plus une nouvelle préparation de ce groupe.
			</p>
			<Button
				type="button"
				severity="secondary"
				label="Marquer comme traité"
				icon={<Check size={16} aria-hidden="true" />}
				loading={isHandling}
				onClick={handleClick}
			/>
		</div>
	);
};

export default MarkHandledAction;
